/**
 * Documentation spaces inside a project (card 4.3, owner 20.10.2026): under a
 * project the Documents sidebar shows "Main" and the project's other spaces.
 *
 * Records live in the PROJECT's bucket (personal or `team:<id>`), extension
 * `projects`, beside the project itself — so a team project's spaces sync to
 * its team and follow projectsStore's write rule (members who are not viewers):
 *
 *   docspace         { id, projectId, name, icon, color, position, created_at, updated_at }
 *   docspace_member  { id: `<projectId>|<nodeId>`, projectId, nodeId, spaceId }
 *
 * ONE space per page (owner decision): the member id is per project + page,
 * so a page has at most one record per project. "Main" is the project's pages
 * with no member record. Deleting a space deletes its member records — its
 * pages go back to Main; no page is ever deleted.
 *
 * Every field is enumerated (an un-enumerated field dies on save).
 */
import { deleteRecord, listRecords, upsertRecord } from '../records/recordsClient';
import { canWriteScope } from '../graph/projectsStore';
import { personalScopeId } from '../services/personalScope';

const EXT = 'projects';
export const SPACE_TYPE = 'docspace';
export const MEMBER_TYPE = 'docspace_member';

const keyOf = (project) => project?.scopeKey || personalScopeId();
const ctx = (project) => ({ workspaceId: keyOf(project), extensionId: EXT });
const uid = () =>
  (typeof crypto !== 'undefined' && crypto.randomUUID && crypto.randomUUID()) || `sp-${Date.now()}-${Math.random().toString(16).slice(2)}`;

function refusal(code, message) {
  return Object.assign(new Error(message), { code });
}
function assertWritable(project) {
  if (!canWriteScope(keyOf(project))) throw refusal('viewer', 'Viewers cannot change team projects.');
}

const strip = ({ record_type, parent_id, sort_key, ...data }) => data;

export function normalizeSpace(draft = {}, projectId) {
  const now = new Date().toISOString();
  return {
    id: String(draft.id || uid()),
    projectId: String(draft.projectId || projectId),
    name: String(draft.name || '').trim() || 'Untitled space',
    icon: draft.icon || null,
    color: draft.color || null,
    position: Number.isFinite(Number(draft.position)) ? Number(draft.position) : 0,
    created_at: draft.created_at || now,
    updated_at: now,
  };
}

export const memberId = (projectId, nodeId) => `${projectId}|${nodeId}`;

/** Every space and member record of one bucket. */
export async function listSpaceRecords(scopeKey) {
  const base = { workspaceId: scopeKey, extensionId: EXT, scopeId: null };
  const [spaces, members] = await Promise.all([
    listRecords({ ...base, recordType: SPACE_TYPE }),
    listRecords({ ...base, recordType: MEMBER_TYPE }),
  ]);
  return {
    spaces: (spaces || []).map(strip).filter((s) => s.id && s.projectId).map((s) => ({ ...s, scopeKey })),
    members: (members || []).map(strip).filter((m) => m.projectId && m.nodeId && m.spaceId),
  };
}

async function putSpace(project, space) {
  await upsertRecord({ ...ctx(project), record: { id: space.id, record_type: SPACE_TYPE, parent_id: null, sort_key: null, data: space } });
  return space;
}

export async function createSpace(project, fields, position = 0) {
  assertWritable(project);
  return putSpace(project, normalizeSpace({ ...fields, position }, project.id));
}

export async function updateSpace(project, space, fields) {
  assertWritable(project);
  return putSpace(project, normalizeSpace({ ...space, ...fields }, project.id));
}

export async function reorderSpaces(project, spaces, orderedIds) {
  assertWritable(project);
  const byId = new Map(spaces.map((s) => [String(s.id), s]));
  for (const [i, id] of orderedIds.entries()) {
    const s = byId.get(String(id));
    // eslint-disable-next-line no-await-in-loop
    if (s && s.position !== i) await putSpace(project, normalizeSpace({ ...s, position: i }, project.id));
  }
}

/** Deletes the space; its pages go back to Main (their member records go). */
export async function removeSpace(project, space, members = []) {
  assertWritable(project);
  for (const m of members) {
    // eslint-disable-next-line no-await-in-loop
    if (m.projectId === String(project.id) && m.spaceId === String(space.id)) await deleteRecord({ ...ctx(project), recordId: memberId(m.projectId, m.nodeId) });
  }
  await deleteRecord({ ...ctx(project), recordId: String(space.id) });
}

/** Puts a page (graph node id) in a space of `project`; `spaceId` null = Main. */
export async function setNodeSpace(project, nodeId, spaceId) {
  assertWritable(project);
  const id = memberId(project.id, nodeId);
  if (!spaceId) {
    await deleteRecord({ ...ctx(project), recordId: id });
    return;
  }
  await upsertRecord({
    ...ctx(project),
    record: { id, record_type: MEMBER_TYPE, parent_id: null, sort_key: null, data: { id, projectId: String(project.id), nodeId, spaceId: String(spaceId) } },
  });
}
